import React, { useState } from 'react';
import { Stack, Typography, Button, Box, Avatar } from '@mui/material';
import { useMutation } from '@apollo/client';
import {
  ACCEPT_EVENT_PLAN_INVITATION,
  DECLINE_EVENT_PLAN_INVITATION,
  GET_EVENT_PLAN_INVITATIONS,
  GET_EVENT_PLANS,
} from './graphql';
import { PVHoverNavy } from '@pv/common/colors';
import {
  AcceptEventPlanInvitationMutation,
  AcceptEventPlanInvitationMutationVariables,
  DeclineEventPlanInvitationMutation,
  DeclineEventPlanInvitationMutationVariables,
  GetEventPlanInvitationsQuery,
} from '@pv/common/graphql/operations';

interface EventPlanInvitationProps {
  invitation: GetEventPlanInvitationsQuery['eventPlanInvitations'][number];
}

export const EventPlanInvitation: React.FC<EventPlanInvitationProps> = ({
  invitation,
}) => {
  const [loading, setLoading] = useState(false);

  const { eventPlan, inviter } = invitation;

  const inviterInitials =
    inviter?.fullName
      ?.split(' ')
      .map((name: string) => name.trim().charAt(0).toLocaleUpperCase())
      .join('')
      .slice(0, 2) || '';

  const [acceptInvitation] = useMutation<
    AcceptEventPlanInvitationMutation,
    AcceptEventPlanInvitationMutationVariables
  >(ACCEPT_EVENT_PLAN_INVITATION, {
    refetchQueries: [GET_EVENT_PLANS, GET_EVENT_PLAN_INVITATIONS],
    onCompleted: () => {
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const [declineInvitation] = useMutation<
    DeclineEventPlanInvitationMutation,
    DeclineEventPlanInvitationMutationVariables
  >(DECLINE_EVENT_PLAN_INVITATION, {
    refetchQueries: [GET_EVENT_PLAN_INVITATIONS],
    onCompleted: () => {
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const handleAccept = async () => {
    setLoading(true);
    await acceptInvitation({
      variables: {
        input: {
          invitationId: invitation.id,
        },
      },
    });
  };

  const handleDecline = async () => {
    setLoading(true);
    await declineInvitation({
      variables: {
        input: {
          invitationId: invitation.id,
        },
      },
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: PVHoverNavy,
        boxShadow: 'none',
        border: '1px solid #E5E8EB',
        borderRadius: '16px',
      }}
    >
      <Stack sx={{ padding: '16px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              src={inviter?.gravatarUrl || undefined}
              alt={inviter?.fullName || 'Inviter avatar'}
              sx={{
                bgcolor: 'primary.main',
              }}
            >
              {inviterInitials}
            </Avatar>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Typography variant="body1" fontWeight={500}>
                {inviter.fullName} invited you to plan{' '}
                {eventPlan.name ? `"${eventPlan.name}"` : 'an event'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" gap="6px">
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={handleDecline}
              disabled={loading}
            >
              Decline
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleAccept}
              disabled={loading}
            >
              Accept
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};
